import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { COURSES, PRICING } from "@/data/courses";
import { api, API } from "@/lib/api";

const Slideshow = ({ images, testId }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <div className="mask-media" data-testid={testId}>
      <AnimatePresence>
        <motion.img
          key={i}
          src={images[i]}
          alt=""
          className="mask-media"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1.16 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.1 }, scale: { duration: 3.8, ease: "linear" } }}
        />
      </AnimatePresence>
    </div>
  );
};

const ClassRow = ({ course, book, onEnroll, flip }) => {
  return (
    <div
      data-testid={`class-section-${course.key.toLowerCase()}`}
      className="relative min-h-screen w-full flex flex-col justify-center border-b border-white/10 py-16"
    >
      {/* Masked video title */}
      <div className="mask-stage h-[50vh] md:h-[62vh] w-full">
        {course.video ? (
          <video
            data-testid={`class-video-${course.key.toLowerCase()}`}
            className="mask-media"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={course.image}
          >
            <source src={course.video} type="video/mp4" />
          </video>
        ) : course.gallery ? (
          <Slideshow
            images={course.gallery}
            testId={`class-slideshow-${course.key.toLowerCase()}`}
          />
        ) : (
          <motion.img
            src={course.image}
            alt={course.key}
            className="mask-media"
            initial={{ scale: 1 }}
            whileInView={{ scale: 1.18 }}
            viewport={{ once: false }}
            transition={{ duration: 14, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          />
        )}
        <div className="mask-cover">
          <h2
            className={`font-display uppercase leading-[0.85] tracking-tighter text-white text-center px-4 break-words ${
              course.key.length > 9
                ? "text-[13vw] md:text-[11vw]"
                : "text-[26vw] md:text-[20vw]"
            }`}
          >
            {course.key}
          </h2>
        </div>
      </div>

      {/* Meta row */}
      <div className="mx-auto mt-10 w-full max-w-[1600px] px-6 md:px-12">
        <div className={`grid gap-8 md:grid-cols-12 items-start ${flip ? "" : ""}`}>
          <div className="md:col-span-2">
            <span className="font-serif-ed text-5xl text-white/20">{course.index}</span>
          </div>
          <div className="md:col-span-6">
            <span className="text-xs uppercase tracking-[0.3em] text-[#7C3AED]">{course.tag}</span>
            <p className="mt-3 text-xl md:text-2xl text-white/80 leading-relaxed max-w-xl">
              {course.blurb}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            {PRICING[course.key] && (
              <div data-testid={`class-price-${course.key.toLowerCase()}`} className="mb-6">
                <div className="font-display text-5xl text-white">{PRICING[course.key].price}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.25em] text-white/50">
                  {PRICING[course.key].note}
                </div>
              </div>
            )}
            <button
              data-testid={`class-apply-${course.key.toLowerCase()}`}
              onClick={() => onEnroll(course.key)}
              className="magnetic-btn inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black hover:border-white"
            >
              Enroll in {course.key}
              <span aria-hidden>→</span>
            </button>
          </div>
          {book && (
            <div
              data-testid={`class-book-${course.key.toLowerCase()}`}
              className="md:col-span-12 mt-2 flex items-center gap-5 rounded-xl border border-white/10 bg-white/[0.03] p-4"
            >
              <img
                src={`${API}/gallery/${book.id}/image`}
                alt={book.title}
                className="h-24 w-20 object-cover object-right rounded"
              />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">Featured Book</div>
                <div className="text-white text-base mt-0.5">{book.title}</div>
                {book.price != null && (
                  <div
                    data-testid={`class-book-price-${course.key.toLowerCase()}`}
                    className="mt-1 font-display text-xl tracking-wide text-white"
                  >
                    {`$${Number(book.price).toFixed(2)}`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ClassShowcase = ({ onEnroll }) => {
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api
      .get("/gallery?category=books")
      .then((r) => setBooks(r.data))
      .catch(() => {});
  }, []);
  const findBook = (key) =>
    books.find((b) => (b.title || "").toLowerCase().includes(key.toLowerCase()));

  return (
    <section id="programs" data-testid="programs-section" className="relative bg-[#050505]">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 pt-28 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">Five Disciplines</span>
          <h2 className="mt-4 font-display text-5xl md:text-7xl uppercase tracking-tight text-white">
            The Programs
          </h2>
        </motion.div>
      </div>
      {COURSES.map((c, i) => (
        <ClassRow
          key={c.key}
          course={c}
          book={findBook(c.key)}
          onEnroll={onEnroll}
          flip={i % 2 === 1}
        />
      ))}
    </section>
  );
};

export default ClassShowcase;
